import { useState } from 'react';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { Container, PageHeader } from '@vnetwork/ui/layout';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Skeleton,
} from '@vnetwork/ui/primitives';
import { DataTable, EmptyState, StatusPill, type ColumnDef } from '@vnetwork/ui/data';
import { Alert, ConfirmDialog, useToast } from '@vnetwork/ui/feedback';
import { useSlaTemplateList, type SlaTemplateFull, type SlaTier } from '@/api/services';
import { useDeleteSlaTemplate } from '@/api/services-mutations';
import { SlaTemplateDrawer } from './sla-template-drawer';

/** Map a tier to a StatusPill tone (StatusTone: neutral | success | warning | danger | info | muted). */
function tierTone(tier: SlaTier): 'neutral' | 'info' | 'success' | 'warning' | 'danger' | 'muted' {
  switch (tier) {
    case 'platinum': return 'success';
    case 'gold': return 'warning';
    case 'silver': return 'info';
    case 'bronze': return 'neutral';
    default: return 'muted';
  }
}

/** Format minutes as "Xh Ym" or "X min". */
function fmtMin(min?: number): string {
  if (!min) return '—';
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

/**
 * SLA Template management — list + create/edit/delete for `/sla-templates`.
 * Endpoints verified live (GET 200, POST/PUT/DELETE available per Go routes).
 * USER triggers create/edit/delete; this page wires the mutations.
 */
export default function SlaTemplatesPage() {
  const toast = useToast();
  const { data: templates = [], isLoading, isError, refetch } = useSlaTemplateList();
  const deleteMutation = useDeleteSlaTemplate();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<SlaTemplateFull | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<SlaTemplateFull | null>(null);

  const openCreate = () => { setEditTarget(null); setDrawerOpen(true); };
  const openEdit = (t: SlaTemplateFull) => { setEditTarget(t); setDrawerOpen(true); };

  const handleDelete = () => {
    if (!deleteTarget) return;
    deleteMutation.mutate(deleteTarget.sla_template_id, {
      onSuccess: () => {
        toast.success(`"${deleteTarget.template_name}" deleted`);
        setDeleteTarget(null);
      },
      onError: (err) =>
        toast.error('Failed to delete template', {
          description: err instanceof Error ? err.message : undefined,
        }),
    });
  };

  const columns: ColumnDef<SlaTemplateFull, unknown>[] = [
    {
      id: 'template_name',
      header: 'Name',
      cell: ({ row }) => (
        <span style={{ fontWeight: 'var(--vn-font-weight-medium)' }}>
          {row.original.template_name}
        </span>
      ),
    },
    {
      id: 'template_tier',
      header: 'Tier',
      cell: ({ row }) => (
        <StatusPill tone={tierTone(row.original.template_tier)}>
          {row.original.template_tier}
        </StatusPill>
      ),
    },
    {
      id: 'uptime_guarantee',
      header: 'Uptime guarantee',
      cell: ({ row }) => `${row.original.uptime_guarantee}%`,
    },
    {
      id: 'default_response_time',
      header: 'Default response',
      cell: ({ row }) => fmtMin(row.original.default_response_time),
    },
    {
      id: 'default_resolution_time',
      header: 'Default resolution',
      cell: ({ row }) => fmtMin(row.original.default_resolution_time),
    },
    {
      id: 'business_hours_only',
      header: 'Business hours',
      cell: ({ row }) => (row.original.business_hours_only ? 'Yes' : 'No'),
    },
    {
      id: 'is_active',
      header: 'Active',
      cell: ({ row }) => (
        <StatusPill tone={row.original.is_active ? 'success' : 'neutral'}>
          {row.original.is_active ? 'Active' : 'Inactive'}
        </StatusPill>
      ),
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => (
        <div style={{ display: 'flex', gap: 'var(--vn-space-1)', justifyContent: 'flex-end' }}>
          <Button
            size="sm"
            variant="ghost"
            onClick={(e) => { e.stopPropagation(); openEdit(row.original); }}
            aria-label="Edit template"
          >
            <Pencil size={13} />
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={(e) => { e.stopPropagation(); setDeleteTarget(row.original); }}
            aria-label="Delete template"
          >
            <Trash2 size={13} />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <Container size="full">
      <PageHeader
        title="SLA Templates"
        description="Reusable service-level agreement tiers — Bronze, Silver, Gold, Platinum, and custom templates."
        actions={
          <Button startIcon={<Plus size={15} />} onClick={openCreate}>
            New template
          </Button>
        }
      />

      <Card flat>
        <CardHeader>
          <CardTitle>Templates</CardTitle>
        </CardHeader>
        <CardBody style={{ padding: 0 }}>
          {isError ? (
            <div style={{ padding: 'var(--vn-space-4)' }}>
              <Alert
                tone="danger"
                title="Failed to load SLA templates"
                actions={<Button size="sm" variant="ghost" onClick={() => refetch()}>Retry</Button>}
              />
            </div>
          ) : isLoading ? (
            <div style={{ padding: 'var(--vn-space-4)', display: 'grid', gap: 'var(--vn-space-2)' }}>
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} style={{ height: 32 }} />
              ))}
            </div>
          ) : (
            <DataTable
              columns={columns}
              data={templates}
              getRowId={(t) => String(t.sla_template_id)}
              onRowClick={openEdit}
              emptyState={
                <EmptyState
                  icon={<Plus size={24} />}
                  title="No SLA templates"
                  description="Create a Bronze, Silver, Gold, or Platinum template to get started."
                />
              }
            />
          )}
        </CardBody>
      </Card>

      {/* Create / Edit drawer */}
      <SlaTemplateDrawer
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        template={editTarget}
      />

      {/* Delete confirm */}
      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}
        title="Delete SLA template"
        description={`Delete "${deleteTarget?.template_name}"? This cannot be undone. Services using this template will retain their configured SLA.`}
        confirmLabel="Delete"
        destructive
        loading={deleteMutation.isPending}
        onConfirm={handleDelete}
      />
    </Container>
  );
}
